"use client"

import { useEffect, useState } from "react"
import { Truck, Shield, Edit3, Grid3X3 } from "lucide-react"
import { HEADER_TRUST_FEATURES, TrustFeatureIcon } from "@constants/navigation"

const ICON_MAP: Record<TrustFeatureIcon, React.ElementType> = {
  truck: Truck,
  shield: Shield,
  edit: Edit3,
  grid: Grid3X3,
}

function TrustFeatureItem({
  feature,
  isFirst,
}: {
  feature: (typeof HEADER_TRUST_FEATURES)[number]
  isFirst: boolean
}) {
  const Icon = ICON_MAP[feature.icon]
  return (
    <div
      className={`flex items-center gap-2 flex-shrink-0 px-4 sm:px-6 ${
        isFirst ? "" : "border-l border-gray-200"
      }`}
    >
      <Icon className="w-6 h-6 text-[#163F85] flex-shrink-0" strokeWidth={1.5} />
      <div className="leading-tight whitespace-nowrap">
        <p className="text-sm font-bold text-[#163F85]">{feature.title}</p>
        <p className="text-xs text-gray-500">{feature.subtitle}</p>
      </div>
    </div>
  )
}

const HeaderTrustBar = () => {
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY < 10)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <div
      className="bg-[#f6f6f6] border-t border-gray-100 w-full overflow-hidden transition-all duration-300 ease-in-out"
      style={{ maxHeight: visible ? "68px" : "0px", opacity: visible ? 1 : 0 }}
    >
      <div className="w-full px-6 sm:px-8 lg:px-12 py-2.5">
        <div className="relative flex overflow-x-hidden">
          <div className="flex items-center animate-marquee whitespace-nowrap sm:animate-none sm:justify-center sm:w-full">
            {HEADER_TRUST_FEATURES.map((feature, i) => (
              <TrustFeatureItem key={feature.icon} feature={feature} isFirst={i === 0} />
            ))}
          </div>

          <div
            className="flex items-center animate-marquee2 whitespace-nowrap sm:hidden"
            aria-hidden="true"
          >
            {HEADER_TRUST_FEATURES.map((feature, i) => (
              <TrustFeatureItem key={feature.icon} feature={feature} isFirst={i === 0} />
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes marquee2 {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
        .animate-marquee2 {
          animation: marquee2 25s linear infinite;
        }
        @media (min-width: 640px) {
          .animate-marquee, .animate-marquee2 {
            animation: none;
          }
        }
      `}</style>
    </div>
  )
}

export default HeaderTrustBar
